/**
 * ============================================================================
 * DEVELOPER MODE UTILITY
 * ============================================================================
 * Утилиты для работы с режимом разработчика
 * Позволяет обходить авторизацию и быстро переключаться между профилями
 * ============================================================================
 */

/**
 * Безопасная система управления режимом разработчика
 */

let devModeState = null

/**
 * Проверка активации режима разработчика с дополнительными проверками
 */
export function isDevModeEnabled() {
	// Кэшируем результат для избежания повторных вычислений
	if (devModeState !== null) {
		return devModeState
	}

	// Поддержка как Vue CLI, так и Vite
	let env = {}
	
	if (typeof process !== 'undefined' && process.env) {
		env = {
			MODE: process.env.NODE_ENV,
			DEV: process.env.NODE_ENV === 'development',
			VUE_APP_DEV_MODE: process.env.VUE_APP_DEV_MODE,
		}
	} else if (typeof import.meta !== 'undefined' && import.meta.env) {
		env = import.meta.env
	}

	// КРИТИЧНО: Режим разработчика работает только в development
	const isDevelopment = env.MODE === 'development' || env.DEV === true
	
	if (!isDevelopment) {
		devModeState = false
		return false
	}

	// Проверяем явное включение через переменную окружения
	const devModeEnv = env.VUE_APP_DEV_MODE === 'true'
	
	// Проверяем localStorage (только если разрешено в .env)
	let devModeStorage = false
	if (devModeEnv) {
		try {
			devModeStorage = localStorage.getItem('dev_mode_enabled') === 'true'
		} catch (e) {
			console.warn('localStorage недоступен для dev mode')
		}
	}

	// Dev mode активен только при ЯВНОМ включении
	devModeState = devModeEnv && devModeStorage
	
	if (devModeState) {
		console.log('🔧 [DevMode] Режим разработчика АКТИВЕН')
		console.log('🔧 [DevMode] Для отключения: localStorage.removeItem("dev_mode_enabled")')
	}

	return devModeState
}

/**
 * Безопасное включение dev mode (только в development)
 */
export function enableDevMode() {
	if (process.env.NODE_ENV !== 'development') {
		console.error('❌ Dev mode доступен только в development режиме')
		return false
	}
	
	if (process.env.VUE_APP_DEV_MODE !== 'true') {
		console.error('❌ Dev mode отключен в .env файле')
		return false
	}
	
	localStorage.setItem('dev_mode_enabled', 'true')
	devModeState = null // Сбрасываем кэш
	console.log('✅ Dev mode включен')
	return true
}

/**
 * Отключение dev mode
 */
export function disableDevMode() {
	localStorage.removeItem('dev_mode_enabled')
	devModeState = null // Сбрасываем кэш
	console.log('✅ Dev mode отключен')
}

/**
 * Сброс состояния dev mode (для HMR)
 */
export function resetDevModeState() {
	devModeState = null
}

// Автоматический сброс при HMR
if (module.hot) {
	module.hot.accept(() => {
		resetDevModeState()
	})
}

/**
 * Переключение режима разработчика
 */
export function toggleDevMode() {
	const isEnabled = isDevModeEnabled()
	if (isEnabled) {
		return disableDevMode()
	} else {
		return enableDevMode()
	}
}

/**
 * Получение текущего профиля разработчика из localStorage
 * @returns {string|null} 'student' | 'teacher' | 'admin' | null
 */
export function getCurrentDevProfile() {
	try {
		return localStorage.getItem('dev_profile')
	} catch (e) {
		return null
	}
}

/**
 * Установка профиля разработчика
 * @param {string} profile - 'student' | 'teacher' | 'admin'
 */
export function setDevProfile(profile) {
	const validProfiles = ['student', 'teacher', 'admin']

	if (!validProfiles.includes(profile)) {
		console.error(
			`❌ [DevMode] Invalid profile: ${profile}. Valid profiles:`,
			validProfiles
		)
		return false
	}

	try {
		localStorage.setItem('dev_profile', profile)
		console.log(`✅ [DevMode] Profile set to: ${profile}`)
		return true
	} catch (e) {
		console.error('❌ [DevMode] Failed to set profile:', e)
		return false
	}
}

/**
 * Очистка профиля разработчика
 */
export function clearDevProfile() {
	try {
		localStorage.removeItem('dev_profile')
		console.log('🗑️ [DevMode] Dev profile cleared')
	} catch (e) {
		console.error('❌ [DevMode] Failed to clear profile:', e)
	}
}

/**
 * Проверка, должен ли router guard пропустить проверку авторизации
 * @returns {boolean}
 */
export function shouldBypassAuth() {
	return isDevModeEnabled()
}

/**
 * Логирование предупреждения о режиме разработчика
 */
export function logDevModeWarning() {
	if (!isDevModeEnabled()) return

	console.log(
		'%c⚠️ DEVELOPER MODE ACTIVE ⚠️',
		'background: #ff6b6b; color: white; font-size: 20px; font-weight: bold; padding: 10px;'
	)
	console.log(
		'%cAuthentication is BYPASSED!',
		'background: #ffa500; color: white; font-size: 14px; padding: 5px;'
	)
	console.log(
		'%cThis mode is ONLY for development. It will be disabled in production.',
		'color: #666; font-size: 12px;'
	)
	console.log('')
	console.log('Available commands:')
	console.log('  - window.devMode.enable()  - Enable dev mode')
	console.log('  - window.devMode.disable() - Disable dev mode')
	console.log('  - window.devMode.toggle()  - Toggle dev mode')
	console.log(
		"  - window.devMode.setProfile('student') - Set profile to student"
	)
	console.log(
		"  - window.devMode.setProfile('teacher') - Set profile to teacher"
	)
	console.log("  - window.devMode.setProfile('admin')   - Set profile to admin")
	console.log('')
}

/**
 * Инициализация режима разработчика
 * Вызывается при загрузке приложения
 */
export function initDevMode() {
	if (!isDevModeEnabled()) return

	// Логируем предупреждение
	logDevModeWarning()

	// Добавляем глобальные команды для удобства
	if (typeof window !== 'undefined') {
		window.devMode = {
			enable: enableDevMode,
			disable: disableDevMode,
			toggle: toggleDevMode,
			setProfile: setDevProfile,
			clearProfile: clearDevProfile,
			getCurrentProfile: getCurrentDevProfile,
			isEnabled: isDevModeEnabled,
		}

		console.log(
			'%c💡 Tip: Use window.devMode for quick access to dev mode commands',
			'color: #4CAF50; font-weight: bold;'
		)
	}
}

/**
 * Проверка, находимся ли мы в production сборке
 * @returns {boolean}
 */
export function isProduction() {
	const env = import.meta?.env || {}
	return env.MODE === 'production' || env.PROD === true
}

/**
 * Безопасная проверка - режим разработчика должен быть отключен в production
 * @returns {boolean}
 */
export function isDevModeSafe() {
	if (isProduction() && isDevModeEnabled()) {
		console.error(
			'🚨 [DevMode] CRITICAL: Developer Mode is enabled in PRODUCTION! This is a security risk!'
		)
		return false
	}
	return true
}

